/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import Loader from 'app/components/atoms/Loader/Loader';
import history from 'store/History';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { closeSidebar } from 'store/actions/sidebar/sidebarActions';
import { mediaBreakpoints } from 'app/themes/breakpoints';
import { setActions, setSubTitle, setTitle } from 'store/actions/sidebar/sidebarActions';
import WorkspaceAboutTab from 'app/containers/Admin/Workspaces/Details/Tabs/WorkspaceAboutTab';
import { loadWorkspace, loadWorkspacePermissions } from 'store/actions/admin/workspacesActions';
import ClassificationsTab from 'app/containers/Common/ClassificationsTab/ClassificationsTab';
import { getStr, isEmpty } from 'app/utils/utils';
import WorkspaceResourcesList from 'app/containers/Admin/Workspaces/WorkspaceResourcesList';
import { get } from 'app/utils/lo/lo';

import { shrinkSidebar, expandSidebar } from 'store/actions/sidebar/sidebarActions';
import WorkspaceHistoryTab from '../Admin/Workspaces/Details/Tabs/WorkspaceHistoryTab';
import withPermissions from 'app/utils/withPermissions';
import WorkspaceMembersList from '../Admin/Workspaces/WorkspaceMembersList';
import WorkspaceLazyResourcesSidebar from '../Admin/Workspaces/WorkspaceLazyResourcesSidebar';

class WorkspaceSidebar extends PureComponent {
    static propTypes = {
        id: PropTypes.string,
        type: PropTypes.string,
        title: PropTypes.string,
        isOpen: PropTypes.bool,
        // from redux
        isLoading: PropTypes.bool,
        details: PropTypes.object,
    };

    validTabs = new Set(['About', 'Classes', 'Entity Resources', 'Task Resources', 'Process Resources',
        'Form Definitions Resources', 'Process Definitions Resources', 'Permissions', 'Scripts',
        'Class Resources', 'Admin', 'History', 'Members']);

    constructor(props) {
        super(props);
        this.initData();
    }

    componentDidUpdate() {
        this.initData();
    }

    @bind
    setActions(expanded, sidebarTitle) {
        this.props.setActions(this.dotMenu(expanded, sidebarTitle));
    }

    initData() {
        const { id, isOpen, isLoading, details, expanded, title } = this.props;
        if (!isOpen) {
            return;
        }
        if (!this.validTabs.has(title)) {
            this.props.setTitle('About');
        }
        this.setActions(expanded, title);
        if (isLoading) {
            return;
        }
        const loaded = details || {};
        if (loaded.id === id) {
            this.props.setSubTitle(details.name);
            return;
        }
        this.props.loadWorkspace(id);
        this.props.loadWorkspacePermissions(id);
    }

    @bind
    onDotMenuClick(title) {
        if (['Collapse', 'Expand'].includes(title)) {
            const { expanded, expandSidebar, shrinkSidebar } = this.props;
            const action = expanded ? shrinkSidebar : expandSidebar;
            action();
        }
        if (title === 'Go to details') {
            this.goToDetails();
        }
    }

    goToDetails() {
        const { windowWidth, id, shrinkSidebar, closeSidebar } = this.props;
        history.push(`/workspaces/${id}`);
        if (!windowWidth < mediaBreakpoints.sm) {
            shrinkSidebar();
        } else {
            closeSidebar();
        }
    }

    @bind
    @memoize()
    dotMenu(expanded) {
        return (
            <DotMenu
                key={13}
                onItemClick={this.onDotMenuClick}
                items={[
                    { name: expanded ? 'Collapse' : 'Expand', icon: expanded ? 'arrow-collapse' : 'open-in-new' },
                    { name: 'Go to details', icon: 'login-variant' },
                ].filter(Boolean)}
            />
        );
    }

    @bind
    @memoize()
    getClassResources(classResources) {
        if (!classResources || isEmpty(classResources)) {
            return [];
        }
        return classResources.map(({ class: cls, role }) => ({ ...cls, role }));
    }

    @bind
    @memoize()
    canEdit(owners) {
        const { id: userId, isAdmin } = this.props.profile;
        if (isAdmin) {
            return true;
        }
        return !!(owners || []).find(({ id }) => id === userId);
    }

    @bind
    buildResourceComponent(type) {
        const { title, details, permissions } = this.props;
        const canEdit = this.canEdit(get(details, 'owners', []));
        const workspaceId = getStr(details, 'id', '');
        const list = type === 'permission' ? (permissions || []) : get(details, 'entityResources', []);
        return <WorkspaceResourcesList title={title} type={type} id={workspaceId} list={list} disabled={!canEdit} />;
    }
    renderContent() {
        const { title, details, openWorkspaceSidebar, reloadList } = this.props;
        const workspaceId = getStr(details, 'id', '');
        const canEdit = this.canEdit(get(details, 'owners', []));
        const filterBy = [{ field: 'primary', op: '=', value: false }];
        switch (title) {
            case 'Classes':
                return <ClassificationsTab details={details} type="workspace" id={workspaceId} name={details.name} isSidebar canEdit={canEdit} filterBy={filterBy} />;
            case 'Entity Resources':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="entity" />;
            case 'Task Resources':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="task" />;
            case 'Process Resources':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="process" />;
            case 'Form Definitions Resources':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="formdefinition" />;
            case 'Process Definitions Resources':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="processdefinition" />;
            case 'Permissions':
                return this.buildResourceComponent('permission');
            case 'Scripts':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="script" />;
            case 'Class Resources':
                return <WorkspaceLazyResourcesSidebar details={details} resourceType="class" />;
            case 'Admin':
                return <WorkspaceResourcesList title={title} type="user" id={workspaceId} disabled={!canEdit} list={get(details, 'owners', [])} />;
            case 'History':
                return <WorkspaceHistoryTab id={workspaceId} />;
            case 'Members':
                return <WorkspaceMembersList details={details} />;
            default:
                return <WorkspaceAboutTab reloadList={reloadList} openWorkspaceSidebar={openWorkspaceSidebar} isSidebar details={details} />;
        }
    }

    render() {
        const { isLoading, details, isOpen } = this.props;
        const isLoaded = details;
        return (
            isOpen && (
                <>
                    {isLoading && <Loader absolute backdrop />}
                    {isLoaded && this.renderContent()}
                </>
            )
        );
    }
}

export default connect(
    (state, ownProps) => ({
        profile: state.user.profile,
        details: state.admin.workspaces.details.data,
        isLoading: state.admin.workspaces.details.isLoading,
        permissions: state.admin.workspaces.permissions.data?.records,
        isOpen: state.sidebar.isOpen,
        expanded: state.sidebar.expanded,
        windowWidth: state.global.window.width,
        actions: state.sidebar.actions,
    }),
    { shrinkSidebar, expandSidebar, loadWorkspace, loadWorkspacePermissions, closeSidebar, setActions, setSubTitle, setTitle }
)(withPermissions(WorkspaceSidebar, 'workspace'));
